import { FaelligkeitFeld, ZuweisungFeld } from '../tasks/AufgabenFelder';
import React, { useEffect, useMemo, useState } from 'react';
import WizardShell from '../ui/wizard-shell';
import { WizardCard, WizardEmpfaenger, WizardKVGrid, WizardKVItem, WizardSection } from '../ui/wizard-layout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { api, historyTemplatesApi } from '../../lib/api';
import { trialRequestsApi, type HideTrialRequestPayload, type TrialRequestHideReasonKey, type TrialRequestItem } from '../../lib/trialRequests';
import { useAuth } from '../auth/use-auth';
import { useStandort } from '../layouts/use-standort';
import { formatDate, formatDateTime } from '../../lib/dateFormat';
import { TAGESENDE, faelligkeitFuerApi, heuteIso } from '../../lib/faelligkeit';
import CommunicationComposer, { type RecipientGroup } from '../communications/CommunicationComposer';

type Step = 1 | 2 | 3;

function reasonLabel(key: TrialRequestHideReasonKey): string {
  if (key === 'not_reachable') return 'nicht erreichbar';
  if (key === 'no_trial_wanted') return 'möchte keine Probestunde';
  return 'Probestunde bereits vereinbart';
}

function templateKeyForReason(key: TrialRequestHideReasonKey): string {
  return `trial.request.hidden.${key}`;
}



export interface TrialRequestHideWizardModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: TrialRequestItem;
  onDone?: (updated: TrialRequestItem) => void;
}

const TrialRequestHideWizardModal: React.FC<TrialRequestHideWizardModalProps> = ({ open, onOpenChange, request, onDone }) => {
  const { user } = useAuth();
  const { selectedStandortId } = useStandort();
  const currentUserId = useMemo(() => (user ? Number.parseInt(user.id, 10) : undefined), [user]);

  const [step, setStep] = useState<Step>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [reasonKey, setReasonKey] = useState<TrialRequestHideReasonKey>('not_reachable');
  const [note, setNote] = useState('');

  const [followUpEnabled, setFollowUpEnabled] = useState(false);
  const [followUpDueDate, setFollowUpDueDate] = useState('');
  const [followUpTimeEnabled, setFollowUpTimeEnabled] = useState(false);
  const [followUpDueTime, setFollowUpDueTime] = useState(TAGESENDE);
  const [followUpAssignedUserId, setFollowUpAssignedUserId] = useState<number | undefined>(currentUserId);
  const [followUpDescription, setFollowUpDescription] = useState<string>('Bei der Probestunden-Anfrage nachfassen – meldet sich der Kunde?');
  const [users, setUsers] = useState<Array<{ id: number; name: string }>>([]);

  // Channel state (driven by CommunicationComposer via onDraftsChange)
  const [channelEmail, setChannelEmail] = useState(false);
  const [channelSms, setChannelSms] = useState(false);
  /** Was im Kommunikationsschritt steht — es geht raus, nicht die Vorlage (T-0032). */
  const [entwurf, setEntwurf] = useState<{ subject: string; html: string; text: string; sms: string } | null>(null);

  const [historyPreviewRendered, setHistoryPreviewRendered] = useState<string>('');

  const canEmail = useMemo(() => !!request.email && /\S+@\S+\.\S+/.test(request.email), [request.email]);
  const canSms = useMemo(() => !!(request.telefon || '').trim(), [request.telefon]);
  const preferredChannels = useMemo(() => {
    const channels = Array.isArray((request as any).communicationChannels) ? (request as any).communicationChannels : [];
    const preferred = channels.filter((c: any) => c && c.officeContactAllowed !== false && c.isActive !== false);
    const hasPreferredEmail = preferred.some((c: any) => String(c.type || '').toLowerCase() === 'email' && String(c.value || '').trim() !== '');
    const hasPreferredSms = preferred.some((c: any) => ['sms', 'mobil'].includes(String(c.type || '').toLowerCase()) && String(c.value || '').trim() !== '');
    return {
      email: hasPreferredEmail && canEmail,
      sms: hasPreferredSms && canSms,
    };
  }, [request, canEmail, canSms]);

  const standortId = useMemo(() => {
    const raw = (request as any)?.standort_id ?? (request as any)?.standortId ?? selectedStandortId ?? null;
    const n = typeof raw === 'string' ? Number.parseInt(raw, 10) : raw;
    return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 0;
  }, [request, selectedStandortId]);

  // Composer groups — single group for the requesting person (trial_request)
  const composerGroups: RecipientGroup[] = useMemo(() => {
    const studentName = `${(request.vorname || '').trim()} ${(request.nachname || '').trim()}`.trim() || 'Interessent';
    // Die E-Mail ist der Regelfall (T-0024); die SMS nur, wenn sie als
    // bevorzugter Kontaktweg gepflegt ist — sie kostet Geld.
    const defaultEmail = canEmail;
    const defaultSms = preferredChannels.sms;

    return [{
      key: 'requester',
      label: 'Interessent',
      recipients: [{
        id: `trial_request:${request.id}`,
        type: 'trial_request' as const,
        trialRequestId: request.id,
        name: studentName,
      }],
      templateKeys: {
        email: templateKeyForReason(reasonKey),
        sms: templateKeyForReason(reasonKey),
      },
      // Dieselben Platzhalter, die der Versand füllt (``anfrage_nachricht``) —
      // sonst zeigt die Vorschau etwas anderes, als beim Empfänger ankommt.
      // ``schoolName`` und ``standortName`` standen hier leer und überschrieben
      // damit die Werte der Engine.
      vars: {
        studentName: studentName || '…',
        studentFirstName: (request.vorname || '').trim(),
        studentLastName: (request.nachname || '').trim(),
        reasonLabel: reasonLabel(reasonKey),
        note: note.trim(),
        studentEmail: (request.email || '').trim(),
        studentPhone: (request.telefon || '').trim(),
        requestId: String(request.id),
      },
      defaultSelectedChannels: { email: defaultEmail, sms: defaultSms },
    }];
  }, [request, reasonKey, note, canEmail, preferredChannels]);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setIsSubmitting(false);
    setReasonKey('not_reachable');
    setNote('');
    setFollowUpEnabled(false);
    setFollowUpAssignedUserId(currentUserId);
    setFollowUpDescription('Bei der Probestunden-Anfrage nachfassen – meldet sich der Kunde?');
    setFollowUpDueDate(heuteIso());
    setFollowUpTimeEnabled(false);
    setFollowUpDueTime(TAGESENDE);
    setEntwurf(null);
    setHistoryPreviewRendered('');
    // Die E-Mail ist der Regelfall — sie war nur an, wenn jemand sie ausdrücklich
    // als bevorzugten Kontaktweg gepflegt hatte, und das tut kaum jemand (T-0024).
    setChannelEmail(canEmail);
    setChannelSms(preferredChannels.sms);
  }, [open, currentUserId, canEmail, preferredChannels]);

  useEffect(() => {
    let cancelled = false;
    const loadTemplate = async () => {
      if (!open) return;
      const studentName = `${(request.vorname || '').trim()} ${(request.nachname || '').trim()}`.trim() || 'Interessent';
      const noteSuffix = reasonKey === 'no_trial_wanted' && note.trim() ? ` – ${note.trim()}` : '';
      try {
        const rendered = await historyTemplatesApi.render({
          template_key: 'trial.request.hidden.history',
          standort_id: standortId || undefined,
          actor_user_id: currentUserId,
          vars: {
            studentName,
            reasonLabel: reasonLabel(reasonKey),
            noteSuffix,
          },
        });
        if (cancelled) return;
        if (rendered.history) setHistoryPreviewRendered(rendered.history);
        if (rendered.task) {
          // Die Aufgabe bleibt explizit user-gesteuert und wird nicht erzwungen.
          if (followUpEnabled && !followUpDescription.trim()) setFollowUpDescription(rendered.task.description || followUpDescription);
          if (!followUpAssignedUserId && rendered.task.assignedUserId) setFollowUpAssignedUserId(rendered.task.assignedUserId);
          if (followUpEnabled && rendered.task.dueAt) setFollowUpDueDate(rendered.task.dueAt.slice(0, 10));
        }
      } catch {
        // fallback handled by local preview
      }
    };
    void loadTemplate();
    return () => {
      cancelled = true;
    };
  }, [open, request, reasonKey, note, currentUserId, followUpEnabled, followUpDescription, followUpAssignedUserId, standortId]);

  // Userliste für Follow-up Assignment
  useEffect(() => {
    let cancelled = false;
    const loadUsers = async () => {
      try {
        const list = await api.get<Array<{ id: number; vorname: string; nachname: string }>>('/api/users');
        if (cancelled) return;
        setUsers(list.map(u => ({ id: u.id, name: `${u.vorname} ${u.nachname}`.trim() })));
      } catch {
        if (!cancelled && currentUserId) {
          setUsers([{ id: currentUserId, name: user?.name || `User #${currentUserId}` }]);
        }
      }
    };
    if (open) loadUsers();
    return () => { cancelled = true; };
  }, [open, currentUserId, user]);

  const canContinueStep1 = useMemo(() => {
    if (reasonKey === 'no_trial_wanted' && note.trim() === '') return false;
    if (followUpEnabled) {
      if (!followUpAssignedUserId) return false;
      if (!followUpDueDate) return false;
    }
    return true;
  }, [reasonKey, note, followUpEnabled, followUpAssignedUserId, followUpDueDate]);

  const historyPreview = useMemo(() => {
    if (historyPreviewRendered) return historyPreviewRendered;
    let desc = `Anfrage ausgeblendet: ${reasonLabel(reasonKey)}`;
    if (reasonKey === 'no_trial_wanted' && note.trim()) {
      const short = note.trim().length > 80 ? note.trim().slice(0, 80) + '…' : note.trim();
      desc += ` – ${short}`;
    }
    return desc;
  }, [historyPreviewRendered, reasonKey, note]);

  // Derive skipSend from channels
  const skipSend = !channelEmail && !channelSms;

  const handleNext = () => {
    if (step === 1) {
      if (reasonKey === 'already_scheduled') setStep(3);
      else setStep(2);
      return;
    }
    if (step === 2) {
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step === 2) return setStep(1);
    if (step === 3) return setStep(reasonKey === 'already_scheduled' ? 1 : 2);
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const payload: HideTrialRequestPayload = {
        reasonKey,
        historyTemplateKey: 'trial.request.hidden.history',
        note: reasonKey === 'no_trial_wanted' ? (note.trim() || null) : null,
        skipSend,
        channels: {
          email: channelEmail,
          sms: channelSms,
        },
        drafts: entwurf ? { requester: entwurf } : undefined,
        followUp: followUpEnabled
          ? {
              enabled: true,
              dueAt: faelligkeitFuerApi(followUpDueDate, followUpTimeEnabled ? followUpDueTime : ''),
              assignedUserId: followUpAssignedUserId,
              description: followUpDescription.trim() || undefined,
            }
          : undefined,
      };
      const updated = await trialRequestsApi.hide(request.id, payload);
      const versandfehler = [updated.emailError, updated.smsError].filter(Boolean).join(' ');
      if (versandfehler) {
        // Ausgeblendet ist sie trotzdem — aber der Versand darf nicht stumm scheitern.
        toast.error(`Anfrage ausgeblendet, aber der Versand klemmt — ${versandfehler}`);
      } else {
        toast.success('Anfrage wurde ausgeblendet');
      }
      onDone?.(updated);
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message || 'Ausblenden fehlgeschlagen');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title="Anfrage ausblenden"
      description={`Schritt ${step} von 3`}
      currentStep={step}
      steps={[{ label: 'Grund' }, { label: 'Benachrichtigung' }, { label: 'Übersicht' }]}
      onBack={step > 1 ? handleBack : undefined}
      canBack={step > 1 && !isSubmitting}
      onNext={step < 3 ? handleNext : undefined}
      canNext={step === 1 ? canContinueStep1 : true}
      onPrimary={step === 3 ? handleConfirm : undefined}
      primaryLabel="Ausblenden"
      canPrimary={!isSubmitting}
      isLoading={isSubmitting}
    >
      <WizardCard className="py-4">
        {step === 1 ? (
          <>
            <WizardSection title="Grund">
              <div className="space-y-2">
                <Label>Warum möchtest du die Anfrage ausblenden?</Label>
                <Select value={reasonKey} onValueChange={(v) => setReasonKey(v as TrialRequestHideReasonKey)}>
                  <SelectTrigger><SelectValue placeholder="Bitte wählen" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_reachable">nicht erreichbar</SelectItem>
                    <SelectItem value="no_trial_wanted">möchte keine Probestunde</SelectItem>
                    <SelectItem value="already_scheduled">Probestunde bereits vereinbart</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {reasonKey === 'no_trial_wanted' ? (
                <div className="mt-4 space-y-2">
                  <Label>Füge weitere Infos hinzu (Pflicht)</Label>
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="z. B. Gründe / Hinweise für spätere Auswertung" rows={4} />
                </div>
              ) : null}
            </WizardSection>

            <WizardSection title="Aufgabe (optional)">
              <div className="flex items-center gap-2">
                <input
                  id="followUpEnabled"
                  type="checkbox"
                  checked={followUpEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setFollowUpEnabled(enabled);
                    if (enabled) {
                      setFollowUpDueDate(heuteIso());
                    }
                  }}
                />
                <Label htmlFor="followUpEnabled">Erstelle eine Aufgabe</Label>
              </div>
              {followUpEnabled ? (
                <div className="mt-4 grid gap-4">
                  <FaelligkeitFeld
                    id="followUp"
                    value={{ datum: followUpDueDate, uhrzeit: followUpTimeEnabled ? followUpDueTime : null }}
                    onChange={(f) => {
                      setFollowUpDueDate(f.datum);
                      setFollowUpTimeEnabled(f.uhrzeit !== null);
                      setFollowUpDueTime(f.uhrzeit ?? TAGESENDE);
                    }}
                  />
                  <ZuweisungFeld
                    id="followUpAssignedUserId"
                    value={followUpAssignedUserId}
                    users={users}
                    onChange={setFollowUpAssignedUserId}
                  />
                  <div className="grid gap-2">
                    <Label>Beschreibung</Label>
                    <Textarea value={followUpDescription} onChange={(e) => setFollowUpDescription(e.target.value)} rows={3} />
                  </div>
                </div>
              ) : null}
            </WizardSection>
          </>
        ) : null}

        {step === 2 ? (
          <WizardSection title="Benachrichtigung">
            <CommunicationComposer
              active={open && step === 2}
              standortId={standortId}
              groups={composerGroups}
              onDraftsChange={({ groups: g }) => {
                const r = g['requester'];
                if (r) {
                  setChannelEmail(!!r.selectedChannels.email);
                  setChannelSms(!!r.selectedChannels.sms);
                  setEntwurf({
                    subject: r.email.subjectTpl || '',
                    html: r.email.htmlTpl || '',
                    text: r.email.textTpl || '',
                    sms: r.sms.smsTpl || '',
                  });
                }
              }}
            />
          </WizardSection>
        ) : null}

        {step === 3 ? (
          <>
            <WizardSection title="Zusammenfassung">
              <WizardKVGrid>
                <WizardKVItem label="Anfrage">
                  {request.vorname} {request.nachname} ({request.email})
                </WizardKVItem>
                {/* Der Grund ist eine Angabe wie jede andere — eine Pille hob ihn
                    ohne Anlass hervor (T-0025). */}
                <WizardKVItem label="Grund">{reasonLabel(reasonKey)}</WizardKVItem>
                {reasonKey === 'no_trial_wanted' && (
                  <WizardKVItem label="Notiz">
                    {note.trim() || <span className="text-muted-foreground">–</span>}
                  </WizardKVItem>
                )}
              </WizardKVGrid>
            </WizardSection>

            <WizardSection title="Benachrichtigungen">
              {/* Wer wird benachrichtigt, und worüber — statt zwei Zeilen „wird
                  versendet" ohne Namen daneben (T-0025). */}
              <WizardEmpfaenger
                name={`${(request.vorname || '').trim()} ${(request.nachname || '').trim()}`.trim() || 'Interessent'}
                kanaele={[
                  { label: 'E-Mail', aktiv: channelEmail },
                  { label: 'SMS', aktiv: channelSms },
                ]}
              />
            </WizardSection>

            <WizardSection title="Eintrag">
              {/* Was in der Akte landet: das Ereignis und, wenn gewünscht, die
                  Aufgabe dazu. Vorher standen beide in getrennten Abschnitten,
                  einer davon „Historie (Preview)" (T-0025). */}
              <div className="text-sm">{historyPreview}</div>
              {followUpEnabled ? (
                <div className="mt-3 border-t pt-3">
                  <WizardKVGrid>
                    <WizardKVItem label="Aufgabe">
                      {followUpDescription.trim() || <span className="text-muted-foreground">–</span>}
                    </WizardKVItem>
                    <WizardKVItem label="Fällig">
                      {followUpDueDate
                        ? followUpTimeEnabled
                          ? formatDateTime(new Date(`${followUpDueDate}T${followUpDueTime}`).toISOString())
                          : formatDate(followUpDueDate)
                        : '–'}
                    </WizardKVItem>
                    <WizardKVItem label="Zugewiesen an">
                      {users.find((u) => u.id === followUpAssignedUserId)?.name || `User #${followUpAssignedUserId || '–'}`}
                    </WizardKVItem>
                  </WizardKVGrid>
                </div>
              ) : null}
            </WizardSection>

            {/* Bestehende Historie wird in der Übersicht bewusst ausgeblendet. */}
          </>
        ) : null}
      </WizardCard>
    </WizardShell>
  );
};

export default TrialRequestHideWizardModal;
